import './dashboard-style.scss';

import { Box, Button, Input, NonCancelableCustomEvent, SpaceBetween } from '@amzn/awsui-components-react';
import { BaseChangeDetail } from '@amzn/awsui-components-react/polaris/input/interfaces';
import { useEffect, useState } from 'react';

import { Partition } from '../../../common/DashboardLocalStorage';
import { constants } from '../../../common/Localization';
import { deepCopy } from '../../../common/util';
import { MAX_PARTITION_COUNT } from './constants';

interface Props {
    partition: Partition;
    setPartition: (partition: Partition) => void;
}

function PartitionTable({ partition, setPartition }: Props): JSX.Element {

    const [ tableRows, setTableRows ] = useState<JSX.Element[]>([]);

    const labelInput = (index: number): JSX.Element => {
        return (
            <Box padding='xs'>
                <Input value={partition.labels[index]}
                    placeholder={constants.CHART_LABEL}
                    onChange={updateLabelAtIndex(index)}
                />
            </Box>
        );
    };

    const boundsInput = (index: number): JSX.Element => {
        return (
            <Input 
                value={partition.upperBounds[index].toString()} 
                type='number'
                onChange={updateUpperBoundAtIndex(index)}
            />
        );
    };

    const addRowButton = (index: number): JSX.Element => {
        return (
            <Button iconName='add-plus' variant='icon' disabled={atMaxBounds()} onClick={() => addRowAtIndex(index)}></Button>
        );
    };

    const loadPartitionData = () => {

        const rows: JSX.Element[] = [];
        for (let index = 0; index < partition.upperBounds.length + 1; index++) {
            rows.push(
                <tr className={index%2 === 0 ? 'highlight-row' : ''} key={index}>
                    {(index > 0) ? <td>{boundsInput(index - 1)}</td> : <td></td>}
                    {index > 0 ? <td>&nbsp;&lt;</td> : <td></td>}
                    <td>{labelInput(index)}</td>
                    {index < partition.upperBounds.length ? <td>&lt;=&nbsp;</td> : <td></td>}
                    {index < partition.upperBounds.length ? <td>{boundsInput(index)}</td> : <td></td>}
                    <td>
                        {partition.upperBounds.length > 0 ? <Button iconName='close' variant='icon' onClick={() => removeRowAtIndex(index)}></Button> : null}
                        {addRowButton(index)}
                    </td>
                </tr>
            );
        };

        if (rows.length === 0) {
            rows.push (
                <tr className='highlight-row'>
                    <td></td>
                    <td></td>
                    <td>{labelInput(0)}</td>
                    <td>
                        {addRowButton(0)}
                    </td>
                </tr>
            );
        }

        setTableRows(rows);
    };

    useEffect(() => {
        loadPartitionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ partition ]);

    const atMaxBounds = () => {
        return partition.upperBounds.length === MAX_PARTITION_COUNT;
    };

    const addRowAtIndex = (index: number) => {
        const updatedPartition: Partition = deepCopy(partition);
        updatedPartition.labels.splice(index, 0, '');
        updatedPartition.upperBounds.splice(index, 0, 0);
        setPartition(updatedPartition);
    };

    const removeRowAtIndex = (index: number) => {
        const updatedPartition: Partition = deepCopy(partition);
        if (updatedPartition.labels.length > 0) {
            updatedPartition.labels.splice(index, 1);
        }
        if (updatedPartition.upperBounds.length > 0) {
            updatedPartition.upperBounds.splice(index < updatedPartition.upperBounds.length ? index : updatedPartition.upperBounds.length - 1, 1);
        }
        setPartition(updatedPartition);
    };

    const updateLabelAtIndex = (index: number) => (event: NonCancelableCustomEvent<BaseChangeDetail>)=> {
        const updatedPartition: Partition = deepCopy(partition);
        updatedPartition.labels[index] = event.detail.value;
        setPartition(updatedPartition);
    };

    const updateUpperBoundAtIndex = (index: number) => (event: NonCancelableCustomEvent<BaseChangeDetail>) => {
        const updatedPartition: Partition = deepCopy(partition);
        updatedPartition.upperBounds[index] = parseInt(event.detail.value);
        setPartition(updatedPartition);
    };

    return (
        <SpaceBetween size='m'>
            <Box textAlign='center'><em>{constants.PARTITION_DESCRIPTION}</em></Box>
            <table>
                <tbody>
                    {tableRows}
                </tbody>
            </table>
        </SpaceBetween>
    );
}

export default PartitionTable;
